/**
 * A workflow as a read-only picture on the console's canvas (flowCanvas.tsx), laid out in waves by the plan graph's layout:
 * a card per step (its kind's icon, its name, who or what works it, and in a run its state as an icon and a word), and the
 * branches between them, labelled with the decision they follow out of an approval. In a run, the branches taken are drawn
 * in the accent, the ones not taken faint and dashed. Pressing a card selects it (the run's page shows that step's tries).
 *
 * The frame's width is measured first, so a long workflow wraps its waves into rows (the plan graph's rule) rather than
 * shrinking to a strip; the frame is then as tall as the picture at the zoom that fits, and the view starts there.
 */
import { BranchesRight, CircleCheck, CircleDashed, CircleMinus, CirclePause, CircleXmark, Clock, Cloud, Code, CodeCommit, CodeMerge, Comment, Eye, Hand, Hourglass, ListCheck, Person, Play, Rocket, Terminal } from '@gravity-ui/icons';
import { type Edge, Handle, type Node, type NodeProps, Position } from '@xyflow/react';
import { type ComponentType, type SVGProps, useEffect, useMemo, useRef, useState } from 'react';
import type { WorkflowDefinition, WorkflowRun, WorkflowState, WorkflowStepRun } from '@aioe/shared';

import { FlowCanvas } from '../flowCanvas.tsx';
import { withNodeSizes } from '../flowMinimap.ts';
import { FLOW_ACCENT, FLOW_HANDLE, FLOW_MUTED, FLOW_TONE, flowEdge } from '../flowStyle.ts';
import { layoutPlanGraph, PLAN_CARD } from '../plans/planGraphLayout.ts';
import { FitText } from '../ui/FitText.tsx';
import { KIND_TONE, type PictureStep, STATE_TONE, stateWords, workflowPicture } from './workflowPicture.ts';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;
const KIND_ICON: Record<PictureStep['kind'], IconType> = { agent: Person, foundry: Cloud, code: Code, approval: Hand, wait: Hourglass, workflow: BranchesRight, checkout: CodeCommit, run: Terminal, checks: ListCheck, comment: Comment, review: Eye, merge: CodeMerge, deploy: Rocket };
/** A shape per state, so the colour is never the only thing that says it. */
const STATE_ICON: Record<WorkflowState, IconType> = { queued: Clock, running: Play, waiting: CirclePause, succeeded: CircleCheck, failed: CircleXmark, cancelled: CircleMinus, skipped: CircleDashed };

type StepNodeData = PictureStep & { selected: boolean } & Record<string, unknown>;

function StepCard({ data }: NodeProps<Node<StepNodeData>>) {
  const Icon = KIND_ICON[data.kind];
  const State = data.state ? STATE_ICON[data.state] : undefined;
  const tone = data.state ? STATE_TONE[data.state] : KIND_TONE[data.kind];
  return (
    <div className="card-art flex h-full w-full flex-col gap-1.5 rounded-xl px-3 py-2.5" data-tone={tone} data-selected={data.selected ? '' : undefined} style={data.selected ? { outline: `2px solid ${FLOW_ACCENT}`, outlineOffset: 2 } : undefined}>
      <Handle type="target" position={Position.Left} className={FLOW_HANDLE} isConnectable={false} />
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-surface-secondary text-muted" aria-hidden="true">
          <Icon className="size-3.5" />
        </span>
        <FitText className="min-w-0 text-sm font-semibold text-foreground">{data.name}</FitText>
      </div>
      <FitText as="div" lines={2} className="text-xs leading-4 text-muted">
        {data.detail}
      </FitText>
      <div className="mt-auto flex min-w-0 items-center gap-1.5 text-xs">
        {State ? <State className="size-3.5 shrink-0" style={{ color: FLOW_TONE[tone] }} aria-hidden="true" /> : null}
        <span className={data.state ? 'font-medium text-foreground' : 'text-muted'}>{stateWords(data)}</span>
        {data.attempts && data.attempts > 1 ? <span className="text-muted tabular-nums">· try {data.attempts}</span> : null}
        {data.actionClass === 'high-impact' ? <span className="ms-auto shrink-0 text-muted">High impact</span> : null}
      </div>
      <Handle type="source" position={Position.Right} className={FLOW_HANDLE} isConnectable={false} />
    </div>
  );
}

const NODE_TYPES = { step: StepCard };
/** Room round the picture inside the frame, the strip along its foot for the zoom buttons, the zoom it never passes, and the frame's least and most height. */
const PAD = { x: 24, y: 20 };
const FOOT = 44;
const MAX_ZOOM = 1;
const FRAME = { min: 200, max: 520 };

export function WorkflowPicture({ definition, run, selected, onSelect }: { definition: WorkflowDefinition; run?: { run: WorkflowRun; steps: readonly WorkflowStepRun[] }; selected?: string; onSelect?: (stepId: string | undefined) => void }) {
  const picture = useMemo(() => workflowPicture(definition, run), [definition, run]);
  const room = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>();
  useEffect(() => {
    const el = room.current;
    if (!el) return;
    const read = () => setWidth((w) => (w === el.clientWidth ? w : el.clientWidth));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const innerWidth = width ? Math.max(1, width - PAD.x * 2) : 0;
  const innerHeight = FRAME.max - PAD.y * 2 - FOOT;
  const graph = useMemo(
    () => layoutPlanGraph(definition.steps.map((s) => ({ ref: s.id, dependsOn: definition.edges.filter((e) => e.to === s.id).map((e) => e.from) })), [], { ...(innerWidth ? { frame: { width: innerWidth, height: innerHeight } } : {}), maxZoom: MAX_ZOOM }),
    [definition, innerWidth, innerHeight],
  );
  const b = graph.bounds;
  const zoom = innerWidth && b.width ? Math.min(MAX_ZOOM, innerWidth / b.width, innerHeight / b.height) : 1;
  const height = Math.min(FRAME.max, Math.max(FRAME.min, Math.ceil(b.height * zoom + PAD.y * 2 + FOOT)));
  // The view starts fitted: the picture centred across the frame and in the room above the zoom buttons.
  const viewport = { zoom, x: PAD.x + (innerWidth - b.width * zoom) / 2 - b.x * zoom, y: PAD.y + (height - PAD.y * 2 - FOOT - b.height * zoom) / 2 - b.y * zoom };
  const flow = useMemo(() => {
    const byId = new Map(picture.steps.map((s) => [s.id, s]));
    const nodes: Node[] = graph.cards.map((c) => ({ id: c.ref, type: 'step', position: { x: c.x, y: c.y }, style: { width: PLAN_CARD.width, height: PLAN_CARD.height }, data: { ...byId.get(c.ref)!, selected: c.ref === selected } satisfies StepNodeData }));
    const via = new Map(graph.edges.map((e) => [`${e.source}->${e.target}`, e.via]));
    const edges: Edge[] = picture.branches.map((br) => {
      const look = br.state === 'taken' ? { stroke: FLOW_ACCENT, width: 2, opacity: 1, zIndex: 1 } : br.state === 'not-taken' ? { stroke: FLOW_MUTED, width: 1, opacity: 0.35, dashed: true } : { stroke: FLOW_MUTED, width: 1.25, opacity: 0.7 };
      const corners = via.get(`${br.from}->${br.to}`);
      return flowEdge({ id: br.id, source: br.from, target: br.to }, { ...look, arrow: 9, ...(br.label ? { label: br.label } : {}), ...(corners ? { via: corners } : {}) });
    });
    return { nodes: withNodeSizes(nodes, { step: PLAN_CARD }), edges };
  }, [picture, graph, selected]);
  // What identifies the picture: a new layout (or frame) is drawn afresh, at its own fitted view; a state that ticks is not.
  const shape = `${definition.id}@${definition.version}:${innerWidth}:${graph.rows}:${height}`;
  return (
    <div ref={room} data-workflow-picture data-rows={graph.rows}>
      {width ? (
        <FlowCanvas
          key={shape}
          frameStyle={{ height }}
          nodes={flow.nodes}
          edges={flow.edges}
          nodeTypes={NODE_TYPES}
          defaultViewport={viewport}
          minZoom={0.2}
          maxZoom={1.5}
          nodesDraggable={false}
          minimap={false}
          footer
          onNodeClick={(_, node) => onSelect?.(node.id === selected ? undefined : node.id)}
          onPaneClick={() => onSelect?.(undefined)}
          aria-label={`The steps of ${definition.name}`}
        />
      ) : (
        <div style={{ height: FRAME.min }} />
      )}
    </div>
  );
}
